import React, { useState, lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import io from "socket.io-client";
const Dashboard = lazy(() => import("./pages/dashboard"));
const TeacherDashboard = lazy(() => import("./pages/teacher/Dashboard"));
const TeacherStudentTracking = lazy(() => import("./pages/teacher/StudentTracking"));
const UserInfo = lazy(() => import("./pages/userInfo"));
const Employinfo = lazy(() => import("./pages/employe"));
import { AI_PLANET_PROXY_WSS, isAuthenticated } from "./utils";
import SignIn from "./pages/login";
import ProtectedRoute from "./utils/protectRoute";
import Layout from "./Layout";
import Toast from "./components/Toast";
import {
  Courses,
  Edit,
  Assessments as AssessmentsCreatorList,
  ManageAssessment,
} from "./pages/course";
import { Diagnostic, Edit as DiagnosticEdit } from "./pages/diagnostic";
import { LessonPlan } from "./pages/course/lessonPlan/LessonPlan";
import { EditLessonPlan } from "./pages/course/lessonPlan/Edit";
import Quiz from "./pages/quiz";
import Profile from "./pages/profile";
import { QuestionBank, Edit as QuestionBankEdit } from "./pages/questionBank";
import { Assessments } from "./pages/assessment";
import { Study } from "./pages/study";
import { CourseTreeView } from "./pages/study/course";
import { GradingModel, Edit as GradingModelEdit } from "./pages/gradingModel";
import { QuizPOC } from "./pages/quizPOC";
import { IRGrading, Edit as IRGradingEdit } from "./pages/irGrading";
import { Edit as IRCourseMapping } from "./pages/irGrading/courseMapping";
import { Student, Edit as StudentEdit } from "./pages/student";
import { Activity } from "./pages/student/Activity";
import { DiagnosticTestV2 } from "./pages/diagnosticTestV2";
import { Activity as MyActivity } from "./pages/activity";
import { SpeechConfig, Edit as SpeechConfigEdit } from "./pages/speechConfig";
import { Reports } from "./pages/activityReport";
import { DiagnosticAssessment } from "./pages/diagnostic/assessment/DiagnosticAssessment";
import useAuth from "./pages/login/hooks";

const App = () => {
  const { auth } = useAuth();
  // Treat an expired token as logged out so the /login route renders the sign-in
  // form instead of redirecting a stale session into the protected app.
  const isAuth = isAuthenticated();

  const [dashboardUrl, setDashboardUrl] = useState("#");

  React.useEffect(() => {
    if (!isAuth) return;

    const socket = io(AI_PLANET_PROXY_WSS, {
      path: "/api/proxy/socket.io",
      transports: ["websocket"],
    });

    window.socketConn = socket;

    socket.on("connect", () => {
      console.log("Connected to Socket.IO server.");
    });

    socket.on("connect_error", (err) => {
      console.error("Socket.IO connection error:", err.message);
    });

    socket.on("error", (err) => {
      console.error("Socket.IO server error:", err);
    });

    socket.on("disconnect", (reason) => {
      console.log("Socket.IO connection disconnected:", reason);
    });

    return () => {
      if (socket.connected) {
        socket.disconnect();
      }
    };
  }, [auth]);

  useEffect(() => {
    if (isAuth) {
      const user = JSON.parse(localStorage.getItem("user"));
      const user_role = user.roles[0].role;

      switch (user_role) {
        case "STUDENT":
          setDashboardUrl("/study");
          break;
        case "CONTENT_ADMIN":
          setDashboardUrl("/diagnostic");
          break;
        case "TEACHER":
          setDashboardUrl("/teacher/dashboard");
          break;
      }
    }
  }, []);

  return (
    <BrowserRouter>
      <Toast />
      <Routes>
        <Route
          path="/login"
          element={isAuth ? <Navigate to={dashboardUrl} replace /> : <SignIn />}
        />
        <Route
          element={
            <ProtectedRoute
              role="user"
              allowedRole={["Admin", "SUPERADMIN", "Editor"]}
            >
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route
            path="/dashboard"
            element={
              <Suspense fallback={<div>Loading Dashboard...</div>}>
                <ProtectedRoute
                  role="admin"
                  allowedRole={["Admin", "SUPERADMIN"]}
                >
                  <Dashboard />
                </ProtectedRoute>{" "}
              </Suspense>
            }
          />
          <Route
            path="/study"
            element={
              <Suspense fallback={<div>Loading Study...</div>}>
                <ProtectedRoute
                  role="admin"
                  allowedRole={["Admin", "SUPERADMIN"]}
                >
                  <Study />
                </ProtectedRoute>{" "}
              </Suspense>
            }
          />
          <Route
            path="/study/course/:id"
            element={
              <Suspense fallback={<div>Loading Course...</div>}>
                <ProtectedRoute
                  role="admin"
                  allowedRole={["Admin", "SUPERADMIN"]}
                >
                  <CourseTreeView />
                </ProtectedRoute>{" "}
              </Suspense>
            }
          />
          <Route
            path="/courses"
            element={
              <Suspense fallback={<div>Loading Course...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <Courses />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/courses/create"
            element={
              <ProtectedRoute
                role="editor"
                allowedRole={["Editor", "SUPERADMIN"]}
              >
                <Edit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/courses/edit/:id"
            element={
              <Suspense fallback={<div>Loading Course...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <Edit />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/diagnostic"
            element={
              <Suspense fallback={<div>Loading Diagnostic...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <Diagnostic />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/diagnostic/create"
            element={
              <ProtectedRoute
                role="editor"
                allowedRole={["Editor", "SUPERADMIN"]}
              >
                <DiagnosticEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/diagnostic/edit/:courseId"
            element={
              <Suspense fallback={<div>Loading Diagnostic...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <DiagnosticEdit />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/diagnostic/:id/assessment"
            element={
              <Suspense fallback={<div>Loading Diagnostic...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <DiagnosticAssessment />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/diagnostic/:courseId/assessment/:planId/section/:sectionId/chapter/:chapterId/create"
            element={
              <Suspense fallback={<div>Loading Assessments...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <ManageAssessment />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/diagnostic/:courseId/assessment/:planId/section/:sectionId/chapter/:chapterId/edit/:assessmentId"
            element={
              <Suspense fallback={<div>Loading Assessments...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <ManageAssessment />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/courses/:courseId/lesson-plan"
            element={
              <Suspense fallback={<div>Loading Lesson Plan...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <EditLessonPlan />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/courses/:id/lesson-plan/create"
            element={
              <Suspense fallback={<div>Loading Lesson Plan...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <EditLessonPlan />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/courses/:id/lesson-plan/edit/:planId"
            element={
              <Suspense fallback={<div>Loading Lesson Plan...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <EditLessonPlan />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/courses/:courseId/lesson-plan/:planId/assessments/section/:sectionId/chapter/:chapterId"
            element={
              <Suspense fallback={<div>Loading Assessments...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <AssessmentsCreatorList />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/courses/:courseId/lesson-plan/:planId/assessments/section/:sectionId/chapter/:chapterId/create"
            element={
              <Suspense fallback={<div>Loading Assessments...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <ManageAssessment />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/courses/:courseId/lesson-plan/:planId/assessments/section/:sectionId/chapter/:chapterId/edit/:assessmentId"
            element={
              <Suspense fallback={<div>Loading Assessments...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <ManageAssessment />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/courses/:courseId/lesson-plan/:planId/assessments/section/:sectionId/create"
            element={
              <Suspense fallback={<div>Loading Assessments...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <ManageAssessment />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/courses/:courseId/lesson-plan/:planId/assessments/section/:sectionId/edit/:assessmentId"
            element={
              <Suspense fallback={<div>Loading Assessments...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <ManageAssessment />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/courses/:courseId/lesson-plan/:planId/assessments/create"
            element={
              <Suspense fallback={<div>Loading Assessments...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <ManageAssessment />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/courses/:courseId/lesson-plan/:planId/assessments/edit/:assessmentId"
            element={
              <Suspense fallback={<div>Loading Assessments...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <ManageAssessment />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/assessments"
            element={
              <Suspense fallback={<div>Loading assessments...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <Assessments />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/quiz/:id"
            element={
              <Suspense fallback={<div>Loading quiz...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <Quiz />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/Userinfo"
            element={
              <Suspense fallback={<div>Loading Dashboard...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <UserInfo />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/Employe"
            element={
              <Suspense fallback={<div>Loading Dashboard...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <Employinfo />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/profile"
            element={
              <Suspense fallback={<div>Loading Profile...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <Profile />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/question-bank"
            element={
              <Suspense fallback={<div>Loading Profile...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <QuestionBank />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/question-bank/create"
            element={
              <ProtectedRoute
                role="editor"
                allowedRole={["Editor", "SUPERADMIN"]}
              >
                <QuestionBankEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/question-bank/edit/:id"
            element={
              <Suspense fallback={<div>Loading Course...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <QuestionBankEdit />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/grading-model"
            element={
              <Suspense fallback={<div>Loading grading model...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <GradingModel />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/grading-model/create"
            element={
              <ProtectedRoute
                role="editor"
                allowedRole={["Editor", "SUPERADMIN"]}
              >
                <GradingModelEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/grading-model/edit/:id"
            element={
              <Suspense fallback={<div>Loading Question...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <GradingModelEdit />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/quiz-poc"
            element={
              <Suspense fallback={<div>Loading Grades...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <QuizPOC />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/ir-grading"
            element={
              <Suspense fallback={<div>Loading grading models...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <IRGrading />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/ir-grading/create"
            element={
              <Suspense fallback={<div>Loading grading model...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <IRGradingEdit />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/ir-grading/edit/:id"
            element={
              <Suspense fallback={<div>Loading grading model...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <IRGradingEdit />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/course-mapping"
            element={
              <Suspense fallback={<div>Loading Grade...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <IRCourseMapping />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/student"
            element={
              <Suspense fallback={<div>Loading Student...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <Student />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/student/edit/:id"
            element={
              <Suspense fallback={<div>Loading Student...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <StudentEdit />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/student/activity/:id"
            element={
              <Suspense fallback={<div>Loading Student...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <Activity />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/student/reports/:studentId"
            element={
              <Suspense fallback={<div>Loading reports...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <Reports />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/diagnostic-test"
            element={
              <Suspense fallback={<div>Loading Diagnostic Test...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <DiagnosticTestV2 />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/my-activity"
            element={
              <Suspense fallback={<div>Loading Student...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <MyActivity />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/speech-config"
            element={
              <Suspense fallback={<div>Loading Profile...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <SpeechConfig />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/speech-config/edit/:id"
            element={
              <Suspense fallback={<div>Loading Course...</div>}>
                <ProtectedRoute
                  role="editor"
                  allowedRole={["Editor", "SUPERADMIN"]}
                >
                  <SpeechConfigEdit />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/teacher/dashboard"
            element={
              <Suspense fallback={<div>Loading Teacher Dashboard...</div>}>
                <ProtectedRoute
                  role="teacher"
                  allowedRole={["TEACHER", "SUPERADMIN"]}
                >
                  <TeacherDashboard />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/teacher/student-tracking"
            element={
              <Suspense fallback={<div>Loading Student Tracking...</div>}>
                <ProtectedRoute
                  role="teacher"
                  allowedRole={["TEACHER", "SUPERADMIN"]}
                >
                  <TeacherStudentTracking />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="/teacher/student-tracking/:studentId"
            element={
              <Suspense fallback={<div>Loading Student Report...</div>}>
                <ProtectedRoute
                  role="teacher"
                  allowedRole={["TEACHER", "SUPERADMIN"]}
                >
                  <Reports />
                </ProtectedRoute>
              </Suspense>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
